/**
 * Hook per gestire WebSocket chat con fallback polling
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { showChatNotification } from '../services/chatNotifications';

interface ChatMessage {
  id?: number;
  from: 'mobile' | 'desktop';
  text: string;
  time: string;
}

interface PhotoUploadedEvent {
  id: string;
  componentId: string;
  turbineId: string;
  photoUrl: string;
  photoOrder: number;
  uploadedAt: string;
}

export interface PhotoRequestCommand {
  type: 'photo';
  context: {
    turbineId: string;
    damageTypeId: number;
    damageName?: string;
  };
  requestedAt: string;
}

interface UseChatWebSocketOptions {
  sessionId: string | null;
  clientType: 'mobile' | 'desktop';
  enabled: boolean; // Attiva solo quando chat è aperta
  onMessageReceived?: (message: ChatMessage) => void; // Callback per nuovi messaggi
  enableNotifications?: boolean; // Abilita notifiche browser
  isChatVisible?: boolean; // Se true, skip notifiche (utente vede già la chat)
  onConnectionStatusChange?: (connected: boolean, clientType: 'mobile' | 'desktop') => void; // Callback per stato connessione altri client
  onPhotoUploaded?: (photo: PhotoUploadedEvent) => void; // Callback per foto caricate
  onPhotoRequest?: (command: PhotoRequestCommand) => void; // Callback per richieste foto dal desktop
}

export const useChatWebSocket = ({ sessionId, clientType, enabled, onMessageReceived, enableNotifications, isChatVisible, onConnectionStatusChange, onPhotoUploaded, onPhotoRequest }: UseChatWebSocketOptions) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [usePolling, setUsePolling] = useState(false);
  const [lastChatId, setLastChatId] = useState<number>(0);

  const wsRef = useRef<WebSocket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef<boolean>(false); // Flag per evitare notifiche su messaggi vecchi

  // Polling fallback
  const startPollingFallback = useCallback(() => {
    if (!sessionId) return;

    console.log('[useChatWebSocket] ⚠️ WebSocket fallito, uso polling fallback');
    setUsePolling(true);

    const pollMessages = async () => {
      try {
        const url = `${window.location.origin}/api/chat/messages/${sessionId}${lastChatId > 0 ? `?since=${lastChatId}` : ''}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id).filter(id => id !== undefined));
              const newMessages = data.messages.filter((m: any) => !existingIds.has(m.id));

              // Marca come inizializzato dopo il primo fetch (PRIMA di processare messaggi)
              const wasInitialized = isInitializedRef.current;
              if (!isInitializedRef.current) {
                isInitializedRef.current = true;
              }

              // Callback e notifiche per nuovi messaggi dall'altro client (solo DOPO inizializzazione)
              if (wasInitialized) {
                newMessages.forEach((msg: ChatMessage) => {
                  if (msg.from !== clientType) {
                    // Notifica browser - SOLO se chat NON visibile
                    if (enableNotifications && !isChatVisible) {
                      console.log('[useChatWebSocket] 🔔 Tentativo notifica (polling):', {
                        permission: Notification.permission,
                        enableNotifications,
                        isChatVisible,
                        from: msg.from
                      });
                      const fromLabel = msg.from === 'desktop' ? 'Operatore' : 'Tecnico Mobile';
                      const notification = showChatNotification({
                        title: `💬 Messaggio da ${fromLabel}`,
                        body: msg.text,
                        tag: 'chat-message'
                      });
                      if (notification) {
                        console.log('[useChatWebSocket] ✅ Notifica mostrata (polling)');
                      } else {
                        console.log('[useChatWebSocket] ❌ Notifica NON mostrata (polling) - permesso:', Notification.permission);
                      }
                    } else if (isChatVisible) {
                      console.log('[useChatWebSocket] 🚫 Chat visibile - skip notifica locale (polling)');
                    }

                    // Callback apertura automatica
                    if (onMessageReceived) {
                      onMessageReceived(msg);
                    }
                  }
                });
              }

              return [...prev, ...newMessages];
            });

            const maxId = Math.max(...data.messages.map((m: any) => m.id));
            setLastChatId(maxId);
          }
        }
      } catch (error) {
        console.error('[useChatWebSocket] Errore polling:', error);
      }
    };

    pollMessages();
    pollingIntervalRef.current = setInterval(pollMessages, 2000);
  }, [sessionId, lastChatId]);

  // Setup WebSocket
  useEffect(() => {
    if (!enabled || !sessionId) return;

    console.log(`[useChatWebSocket] 🔌 Connessione WebSocket (${clientType})...`);

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/chat`);
    wsRef.current = ws;

    let authTimeout: NodeJS.Timeout;

    ws.onopen = () => {
      console.log(`[useChatWebSocket] ✅ WebSocket connesso (${clientType})`);
      setWsConnected(true);

      // Autentica
      ws.send(JSON.stringify({
        type: 'auth',
        sessionId,
        clientType
      }));

      // Timeout autenticazione
      authTimeout = setTimeout(() => {
        if (!wsConnected) {
          console.warn('[useChatWebSocket] ⚠️ Timeout autenticazione, uso polling');
          ws.close();
          startPollingFallback();
        }
      }, 5000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'auth_success':
            clearTimeout(authTimeout);
            console.log('[useChatWebSocket] ✅ Autenticazione riuscita');
            // Marca come inizializzato quando WebSocket è pronto
            isInitializedRef.current = true;
            break;

          case 'chat_message':
            console.log('[useChatWebSocket] 💬 Messaggio via WebSocket:', data.message);
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id).filter(id => id !== undefined));
              if (data.message.id && existingIds.has(data.message.id)) {
                return prev;
              }
              return [...prev, data.message];
            });

            if (data.message.id) {
              setLastChatId(prev => Math.max(prev, data.message.id));
            }

            // Beep se messaggio da desktop (solo mobile)
            if (clientType === 'mobile' && data.message.from === 'desktop') {
              try {
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                oscillator.frequency.value = 600;
                oscillator.type = 'sine';
                gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + 0.1);
              } catch (e) {
                // Ignore
              }
            }

            // Callback per apertura automatica chat e notifiche
            if (data.message.from !== clientType) {
              // Notifica browser - SOLO se chat NON visibile
              if (enableNotifications && !isChatVisible) {
                console.log('[useChatWebSocket] 🔔 Tentativo notifica:', {
                  permission: Notification.permission,
                  enableNotifications,
                  isChatVisible,
                  from: data.message.from
                });
                const fromLabel = data.message.from === 'desktop' ? 'Operatore' : 'Tecnico Mobile';
                const notification = showChatNotification({
                  title: `💬 Messaggio da ${fromLabel}`,
                  body: data.message.text,
                  tag: 'chat-message'
                });
                if (notification) {
                  console.log('[useChatWebSocket] ✅ Notifica mostrata');
                } else {
                  console.log('[useChatWebSocket] ❌ Notifica NON mostrata - permesso:', Notification.permission);
                }
              } else if (isChatVisible) {
                console.log('[useChatWebSocket] 🚫 Chat visibile - skip notifica locale');
              }

              // Callback apertura automatica
              if (onMessageReceived) {
                onMessageReceived(data.message);
              }
            }
            break;

          case 'clear_chat':
            console.log('[useChatWebSocket] 🗑️ Ricevuto clear_chat, cancello messaggi locali');
            setMessages([]);
            setLastChatId(0);
            break;

          case 'connection_status':
            // Evento connessione/disconnessione altro client (mobile/desktop)
            console.log('[useChatWebSocket] 📡 Connection status event:', {
              connected: data.connected,
              clientType: data.clientType,
              timestamp: data.timestamp
            });

            // Notifica solo se altro client (non noi stessi)
            if (data.clientType && data.clientType !== clientType && onConnectionStatusChange) {
              onConnectionStatusChange(data.connected, data.clientType);
            }
            break;

          case 'photo_uploaded':
            // Evento foto caricata - notifica al frontend per refresh UI
            console.log('[useChatWebSocket] 📸 Photo uploaded event:', data.photo);
            if (onPhotoUploaded && data.photo) {
              onPhotoUploaded(data.photo);
            }
            break;

          case 'request_photo':
            // Evento richiesta foto dal desktop - notifica al mobile
            console.log('[useChatWebSocket] 📷 Photo request event:', data.command);
            if (onPhotoRequest && data.command) {
              onPhotoRequest(data.command);
            }
            break;

          case 'pong':
            break;

          default:
            console.log('[useChatWebSocket] Messaggio:', data.type);
        }
      } catch (error) {
        console.error('[useChatWebSocket] Errore parsing:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[useChatWebSocket] ❌ Errore WebSocket:', error);
    };

    ws.onclose = () => {
      console.log('[useChatWebSocket] 🔌 WebSocket disconnesso');
      setWsConnected(false);

      if (enabled && !usePolling) {
        startPollingFallback();
      }
    };

    return () => {
      console.log('[useChatWebSocket] Cleanup');
      clearTimeout(authTimeout);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      ws.close();
    };
  }, [enabled, sessionId, clientType, startPollingFallback]);

  // Invia messaggio
  const sendMessage = useCallback(async (text: string) => {
    if (!sessionId || !text.trim()) return;

    if (wsConnected && wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[useChatWebSocket] 📤 Invio via WebSocket');
      wsRef.current.send(JSON.stringify({
        type: 'chat_message',
        from: clientType,
        text: text.trim()
      }));
    } else {
      console.log('[useChatWebSocket] 📤 Invio via HTTP');
      try {
        await fetch(`${window.location.origin}/api/chat/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            fromType: clientType,
            message: text.trim()
          })
        });
      } catch (error) {
        console.error('[useChatWebSocket] Errore invio:', error);
      }
    }
  }, [sessionId, clientType, wsConnected]);

  // Clear messages localmente (per "cancella solo per me")
  const clearMessagesLocally = useCallback(() => {
    console.log('[useChatWebSocket] 🗑️ Cancello messaggi localmente');
    setMessages([]);
    setLastChatId(0);
  }, []);

  return {
    messages,
    wsConnected,
    usePolling,
    sendMessage,
    clearMessagesLocally
  };
};
